/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { FastNumberInput, cn, useLocalization } from "@lemmonhq/uikit";
import { GiftCardSection } from "./Section";

export type GiftCardAmountPickerProps = {
  value: number | null;
  /** Quick amounts, shown as chips in this order. */
  presets: number[];
  currency: string;
  onChange: (value: number | null) => void;
};

/**
 * The amount of a gift card or table credit: common amounts as chips, any
 * other number through the numpad.
 */
export const GiftCardAmountPicker = defineComponent<GiftCardAmountPickerProps>(
  {
    name: "GiftCardAmountPicker",
    props: ["value", "presets", "currency", "onChange"],
    setup(props) {
      const { t } = useLocalization();
      const isCustom = computed(
        () => props.value != null && !props.presets.includes(props.value),
      );

      return () => (
        <GiftCardSection icon="payments" title={t("general.words.amount")}>
          <div
            class="grid gap-1.5 [grid-template-columns:repeat(var(--chips),minmax(0,1fr))] sm:gap-2"
            style={{ "--chips": props.presets.length }}
          >
            {props.presets.map((amount) => {
              const selected = props.value === amount;
              return (
                <button
                  key={amount}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => props.onChange(amount)}
                  class={cn(
                    // Equal squares; the amount shrinks with the chip so
                    // four digits ("CHF 1000") still fit on a phone.
                    "relative flex aspect-square min-w-0 flex-col items-center justify-center overflow-hidden rounded-xl border px-1 transition-all active:scale-[0.97]",
                    selected
                      ? "border-primary bg-primary text-primary-content shadow-md"
                      : "border-base-300 bg-base-200 text-accent hover:border-primary/50 hover:bg-base-300",
                  )}
                >
                  <span class="text-[10px] font-medium uppercase leading-none tracking-wider opacity-70">
                    {props.currency}
                  </span>
                  <span class="mt-0.5 max-w-full truncate text-base font-bold tabular-nums leading-tight sm:text-lg">
                    {amount}
                  </span>
                  {selected ? (
                    <span class="material-symbols-outlined absolute right-1 top-1 text-xs">
                      check_circle
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div class="relative">
            <span
              class={cn(
                "material-symbols-outlined pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg",
                isCustom.value ? "text-primary" : "text-base-content/50",
              )}
            >
              dialpad
            </span>
            <FastNumberInput
              value={isCustom.value ? props.value : null}
              onChange={(value: number | null) => {
                if (value != null && value > 0) props.onChange(value);
              }}
              min={1}
              // A side popover has no room next to a full-width row.
              showInModal={true}
              simplified={true}
              simplifiedLabel={t("pos.gift_card.custom_amount")}
              placeholder={t("pos.gift_card.custom_amount")}
              class={cn(
                "min-h-12 rounded-xl pl-10",
                isCustom.value
                  ? "border-primary bg-primary/10"
                  : "border-dashed bg-base-100",
              )}
            />
          </div>
        </GiftCardSection>
      );
    },
  },
);
